import { Text, View, XStack } from "tamagui";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { currentUserAtom } from "../../../../atoms/global";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader } from "../../../components/design-system/Loader";
import { HeaderButton } from "../../../components/design-system/Button/HeaderButton";
import { CloseIcon } from "../../../components/design-system/icons";
import { RideForm } from "../../../components/RideForm/RideForm";
import { StackActions, useNavigation } from "@react-navigation/native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { BaseNavigatorParamList } from "../../../navigators/BaseNavigator";
import { Ride } from "../../../components/RideForm/types";
import { api, useApiQuery, useApiQueryClient } from "../../../../lib/api";
import { Event } from "../../../types/event";
import { useDeleteEvent } from "../EventDetail/hooks/useDeleteEvent";
import { ConfirmationModal } from "../../../components/design-system/ConfirmationModal";
import { EventDetailDeleteButton } from "../EventDetail/components/EventDetailDeleteButton";

type EditEventProps = NativeStackScreenProps<
  BaseNavigatorParamList,
  "EditEvent"
>;

export const EditEvent = (props: EditEventProps) => {
  const eventId = props.route.params.eventId;

  const { data: eventData } = useApiQuery<{ event: Event }>(
    ["events", eventId, "edit"],
    `/api/v1/events/${eventId}`,
    {
      refetchOnMount: "always",
    }
  );

  const event = eventData?.event;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const currentUser = useAtomValue(currentUserAtom);

  const navigation = useNavigation();
  const queryClient = useApiQueryClient();
  const { top, bottom } = useSafeAreaInsets();

  const [deleteEvent, isDeleting] = useDeleteEvent(() => {
    navigation.dispatch(StackActions.pop(2));
  });

  const handleDeleteEvent = () => {
    if (event?.id) deleteEvent(event.id);
  };

  const { mutateAsync: updateEvent, isPending: isUpdating } = useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.patch(`/api/v1/events/${eventId}`, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["events", eventId] });
      navigation.goBack();
    },
  });

  const onSubmit = async (values: Ride) => {
    const { route, invited_users, ...rest } = values;
    const input = {
      ...rest,
      route_id: values.route?.id,
    };
    await updateEvent(input);
  };

  const defaultValues: Ride = {
    name: event?.name ?? "",
    description: event?.description ?? "",
    date: event?.date ?? "",
    time: event?.time ?? "",
    groups: event?.groups ?? [],
    terrain: event?.terrain ?? "Road",
    visibility: event?.visibility ?? "Public",
    invited_users: [],
    route: event?.route ?? undefined,
    location: event?.location ?? "",
  };

  return (
    <View flex={1} bg="$brandParchment" paddingTop={top}>
      <View px="$4" pb="$4" justifyContent="flex-start" alignItems="flex-start">
        <XStack justifyContent="space-between" w="100%" alignItems="center">
          <View>
            <HeaderButton onPress={() => navigation.goBack()}>
              <CloseIcon color="black" width={16} height={16} />
            </HeaderButton>
          </View>
          <View justifyContent="flex-end" alignItems="flex-end">
            <Text fontSize={16}>Edit Ride</Text>
          </View>
        </XStack>
      </View>
      {isUpdating || isDeleting ? <Loader /> : null}
      <RideForm
        currentUserId={currentUser?.id ?? ""}
        handleSubmit={onSubmit}
        defaultValues={defaultValues}
        isEditing
        footer={
          <EventDetailDeleteButton
            isVisible={true}
            onPress={() => setIsDeleteModalOpen(true)}
          />
        }
      />
      <ConfirmationModal
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteEvent}
        isOpen={isDeleteModalOpen}
        title="Delete Event?"
        subtitle="Are you sure you want to delete this ride?"
        yesMessage="YES!"
        noMessage="NO!"
      />
    </View>
  );
};
